import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCurrencySymbol } from '../../utils/currency';

interface DeveloperData {
    id: string;
    name: string;
}

interface DeveloperPaymentFormData {
    developerId: string;
    amount: number | string;
    bankCharges?: number | string;
    paymentDate: string;
    paymentMode: string;
    depositFrom: string;
    reference?: string;
    notes?: string;
}

interface DeveloperPaymentFormProps {
    onClose: () => void;
    onSuccess: () => void;
    payment?: any;
    developers: DeveloperData[];
}

const DeveloperPaymentForm: React.FC<DeveloperPaymentFormProps> = ({ onClose, onSuccess, payment, developers }) => {
    const { currency } = useAuth();
    const { register, handleSubmit, reset, formState: { errors } } = useForm<DeveloperPaymentFormData>({
        defaultValues: {
            developerId: '',
            amount: '',
            bankCharges: '',
            paymentDate: new Date().toISOString().split('T')[0],
            paymentMode: 'Bank Transfer',
            depositFrom: 'Main Account',
            reference: '',
            notes: ''
        }
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (payment) {
            reset({
                ...payment,
                paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0]
            });
        }
    }, [payment, reset]);

    const onSubmit = async (data: DeveloperPaymentFormData) => {
        try {
            setIsSubmitting(true);
            const api = (await import('../../services/api')).default;
            if (payment?.id) {
                await api.put(`/developer-payments/${payment.id}`, data);
            } else {
                await api.post('/developer-payments', data);
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving developer payment:', error);
            alert('Failed to save developer payment');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">
                        {payment ? 'Edit Developer Payment' : 'New Developer Payment'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    <form id="developerPaymentForm" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        {/* Developer Info */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Payment Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Developer *</label>
                                    <select
                                        {...register('developerId', { required: 'Developer is required' })}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 bg-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    >
                                        <option value="">Select a developer...</option>
                                        {developers.map(d => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </select>
                                    {errors.developerId && <p className="mt-1 text-sm text-red-600">{errors.developerId.message}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">{getCurrencySymbol(currency)}</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            {...register('amount', { required: 'Amount is required' })}
                                            className="w-full pl-7 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Charges</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">{getCurrencySymbol(currency)}</span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            {...register('bankCharges')}
                                            className="w-full pl-7 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Method */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Method & Source</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                                    <input
                                        type="date"
                                        {...register('paymentDate', { required: 'Date is required' })}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    />
                                    {errors.paymentDate && <p className="mt-1 text-sm text-red-600">{errors.paymentDate.message}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode *</label>
                                    <select
                                        {...register('paymentMode', { required: 'Mode is required' })}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    >
                                        <option value="Bank Transfer">Bank Transfer</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Credit Card">Credit Card</option>
                                        <option value="PayPal">PayPal</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Source Account *</label>
                                    <input
                                        type="text"
                                        {...register('depositFrom', { required: 'Source account is required' })}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        placeholder="e.g. Main Business Checking"
                                    />
                                    {errors.depositFrom && <p className="mt-1 text-sm text-red-600">{errors.depositFrom.message}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference #</label>
                                    <input
                                        type="text"
                                        {...register('reference')}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        placeholder="Transaction ID / Check #"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                            <textarea
                                {...register('notes')}
                                rows={3}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                placeholder="Add any private notes regarding this payment..."
                            />
                        </div>
                    </form>
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="developerPaymentForm"
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors shadow-sm"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        ) : (
                            <Save size={18} />
                        )}
                        {payment ? 'Update Payment' : 'Save Payment'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeveloperPaymentForm;
